import { getSentryModule, isSentryInitialized } from './sentryService';

interface MetricCaptureOptions {
  attributes?: Record<string, string>;
  unit?: string;
}

interface MetricsApi {
  count: (name: string, value: number, options: MetricCaptureOptions) => void;
  distribution: (
    name: string,
    value: number,
    options: MetricCaptureOptions
  ) => void;
}

/**
 * Sentry's metrics API is experimental (`@experimental` in the SDK, gated on the
 * currently-default-on `enableMetrics` flag) and may change shape across majors,
 * so we probe the loaded module instead of importing `metrics` statically. We
 * read the copy `sentryService` already loaded rather than firing our own
 * `import('@sentry/react')` — that separate import resolves a tick late and
 * silently dropped the first metrics emitted right after init (e.g. `app.start`).
 * Returns null when telemetry is off or a future SDK drops/renames the surface,
 * in which case callers no-op.
 */
const getMetricsApi = (): MetricsApi | null => {
  const Sentry = getSentryModule();
  if (!Sentry) {
    return null;
  }

  const metrics = (Sentry as { metrics?: Partial<MetricsApi> }).metrics;
  if (
    !metrics ||
    typeof metrics.count !== 'function' ||
    typeof metrics.distribution !== 'function'
  ) {
    return null;
  }

  return metrics as MetricsApi;
};

const buildOptions = (
  tags: Record<string, string>,
  unit?: string
): MetricCaptureOptions => {
  const options: MetricCaptureOptions = {};
  if (Object.keys(tags).length > 0) {
    options.attributes = tags;
  }
  if (unit) {
    options.unit = unit;
  }
  return options;
};

export const incrementCounter = (
  name: string,
  value = 1,
  tags: Record<string, string> = {},
  unit?: string
): void => {
  if (!isSentryInitialized()) {
    return;
  }

  const metrics = getMetricsApi();
  if (!metrics) {
    return;
  }

  try {
    metrics.count(name, value, buildOptions(tags, unit));
  } catch (error) {
    console.warn('Failed to increment counter metric in Sentry:', error);
  }
};

export const recordDistribution = (
  name: string,
  value: number,
  tags: Record<string, string> = {},
  unit?: string
): void => {
  if (!isSentryInitialized()) {
    return;
  }

  const metrics = getMetricsApi();
  if (!metrics) {
    return;
  }

  try {
    metrics.distribution(name, value, buildOptions(tags, unit));
  } catch (error) {
    console.warn('Failed to record distribution metric in Sentry:', error);
  }
};
